import {
  all, AllEffect, ForkEffect, put, select, spawn, takeEvery
} from "redux-saga/effects";

import { IProject } from "Model/Storage/IProject";
import { PayloadAction } from "@reduxjs/toolkit";
import { IMeta } from "Model/State/INotificationMeta";
import { hasNoElements } from "Utils/Array";
import { walk } from "Utils/ObjectWalker";
import { ICompany } from "Model/Storage/ICompany";
import { IContact } from "Model/Storage/IContact";
import { updateProjectItem } from "Utils/Item";
import { countRelevantItems } from "Utils/Relevance";
import { filterRecord, recordToArray } from "Utils/Record";
import { selectProjectMap, selectSelectedOrActiveProjects } from "../Selectors/Projects";
import {
  projectsContactRelevanceStart,
  projectsCompanyRelevanceError,
  projectsCompanyRelevanceStart,
  projectsCompanyRelevanceSuccess,
  projectsContactRelevanceError,
  projectsContactRelevanceSuccess,
  projectsRelevanceError,
  projectsRelevanceStart,
  projectsRelevanceSuccess,
  projectsUpdateStart
} from "../Actions";
import { sortAndFilterSaga } from "./Projects";

type ProjectsRelevanceStartAction = PayloadAction<string[], "projects/relevance/start", IMeta>;
function* projectsRelevanceStartSaga({ payload }: ProjectsRelevanceStartAction) {
  try {
    const selectedProjects: IProject[] = yield getProjects(payload);
    yield put(
      projectsUpdateStart(
        selectedProjects.map((project: IProject) => {
          const updatedProject: IProject = {
            ...project,
            "@isRelevant": !project[`@isRelevant`]
          };

          updatedProject[`@relevantCount`] = countRelevantItems(project);
          return updatedProject;
        }),
        true
      )
    );

    yield all([put(projectsRelevanceSuccess()), spawn(sortAndFilterSaga)]);
  } catch (e) {
    console.error(e);
    yield put(projectsRelevanceError());
  }
}

type ProjectsCompanyRelevanceStartAction = PayloadAction<string, "projects/company/relevance/start", IMeta>;
function* projectsCompanyRelevanceStartSaga({ payload }: ProjectsCompanyRelevanceStartAction) {
  try {
    const [project]: IProject[] = yield select(selectSelectedOrActiveProjects);

    const updatedProject = updateProjectItem(project, payload, (company: ICompany) => ({
      ...walk(company, `contacts`, null, (contact: IContact) => ({
        ...contact,
        "@isRelevant": !company[`@isRelevant`]
      })),
      "@isRelevant": !company[`@isRelevant`]
    }));

    updatedProject[`@relevantCount`] = countRelevantItems(updatedProject);
    yield put(projectsUpdateStart([updatedProject], true));

    yield put(projectsCompanyRelevanceSuccess());
  } catch (error) {
    console.error(error);
    yield put(projectsCompanyRelevanceError());
  }
}

type ProjectsContactRelevanceStartAction = PayloadAction<string, "projects/company/contact/relevance/start", IMeta>;
function* projectsCompanyContactRelevanceStartSaga({ payload }: ProjectsContactRelevanceStartAction) {
  try {
    const [project]: IProject[] = yield select(selectSelectedOrActiveProjects);

    const updatedProject = updateProjectItem(project, payload, (contact: IContact) => ({
      ...contact,
      "@isRelevant": !contact[`@isRelevant`]
    }));

    updatedProject[`@relevantCount`] = countRelevantItems(updatedProject);
    yield put(projectsUpdateStart([updatedProject], true));
    yield put(projectsContactRelevanceSuccess());
  } catch (error) {
    console.error(error);
    yield put(projectsContactRelevanceError());
  }
}

function* getProjects(idlist: string[]) {
  if (hasNoElements(idlist)) {
    const projects = yield select(selectSelectedOrActiveProjects);
    return projects;
  }

  const projectMap: Record<string, IProject> = yield select(selectProjectMap);
  return recordToArray(filterRecord(projectMap, idlist));
}

export function* RelevanceSaga(): Generator<AllEffect<ForkEffect<never>>, void, unknown> {
  yield all([
    takeEvery(projectsRelevanceStart, projectsRelevanceStartSaga),
    takeEvery(projectsCompanyRelevanceStart, projectsCompanyRelevanceStartSaga),
    takeEvery(projectsContactRelevanceStart, projectsCompanyContactRelevanceStartSaga)
  ]);
}
